import { useState, useEffect, useCallback, useMemo } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { WorkOrder, WorkOrderStatus, getComponentKeys } from '../../types/workOrders';
import { Issue } from '../../types/issues';
import { ProjectModel, ProjectStatus } from '../../types/projects';
import { workOrdersApi } from '../../api/workOrdersApi';
import { issuesApi } from '../../api/issuesApi';
import { projectsApi, ProjectWithBuilding } from '../../api/projectsApi';
import { BuildingCheckWithTemplate, parseBuildingCheck } from '../../Pages/BuildingChecks/buildingChecksUtils';
import { BuildingCheckTemplate } from '../../Pages/BuildingChecks/types';
import type { CostSource } from '../../Helpers/maintenanceCostUtils';

// Maintenance activity from maintenance plan
export interface ComponentMaintenanceActivity {
    templateId: string;
    templateName: string;
    nextDate: string;
    cost?: number;
    costSource?: CostSource;
    intervalType?: string;
    intervalValue?: number;
}

export interface UseComponentRelatedItemsResult {
    // Active items
    workOrders: WorkOrder[];
    issues: Issue[];
    maintenanceActivities: ComponentMaintenanceActivity[];
    inspections: BuildingCheckWithTemplate[];
    projects: ProjectWithBuilding[];

    // Archived items (lazy-loaded)
    archivedWorkOrders: WorkOrder[];
    archivedIssues: Issue[];

    // State
    isLoading: boolean;
    isLoadingArchived: boolean;
    error: string | null;
    showArchived: boolean;
    setShowArchived: (show: boolean) => void;

    // Actions
    refetch: () => Promise<void>;

    // Computed
    hasAnyItems: boolean;
    hasArchivedItems: boolean;
}

export function useComponentRelatedItems(
    buildingKey: string,
    componentKey: string
): UseComponentRelatedItemsResult {
    const db = getDatabase();

    // Active data
    const [allWorkOrders, setAllWorkOrders] = useState<WorkOrder[]>([]);
    const [allIssues, setAllIssues] = useState<Issue[]>([]);
    const [maintenanceActivities, setMaintenanceActivities] = useState<ComponentMaintenanceActivity[]>([]);
    const [allInspections, setAllInspections] = useState<BuildingCheckWithTemplate[]>([]);
    const [allProjects, setAllProjects] = useState<ProjectWithBuilding[]>([]);

    // Archived data (lazy-loaded)
    const [archivedWorkOrders, setArchivedWorkOrders] = useState<WorkOrder[]>([]);
    const [archivedIssues, setArchivedIssues] = useState<Issue[]>([]);
    const [archivedLoaded, setArchivedLoaded] = useState(false);

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingArchived, setIsLoadingArchived] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showArchived, setShowArchived] = useState(false);

    // Load maintenance activities for this component
    const loadMaintenanceActivities = useCallback(async (): Promise<ComponentMaintenanceActivity[]> => {
        try {
            const [planSnapshot, templatesSnapshot] = await Promise.all([
                get(ref(db, `maintenance/active/${buildingKey}`)),
                get(ref(db, 'config/maintenanceTemplates'))
            ]);

            if (!planSnapshot.exists()) {
                return [];
            }

            const plan = planSnapshot.val();
            const componentData = plan?.components?.[componentKey];

            if (!componentData?.maintenanceActivities) {
                return [];
            }

            // Build template name lookup from flat maintenanceTemplates
            const templateNames: Record<string, string> = {};
            if (templatesSnapshot.exists()) {
                const templatesData = templatesSnapshot.val();
                for (const [id, template] of Object.entries(templatesData)) {
                    const t = template as Record<string, unknown>;
                    if (t.name && typeof t.name === 'string') {
                        templateNames[id] = t.name;
                    }
                }
            }

            const activities: ComponentMaintenanceActivity[] = [];

            for (const [templateId, activity] of Object.entries(componentData.maintenanceActivities)) {
                const activityData = activity as Record<string, unknown>;

                // Skip disabled activities
                if (activityData.disabled) {
                    continue;
                }

                // Look up template name from flat templates structure
                const templateName = templateNames[templateId] || templateId;

                activities.push({
                    templateId,
                    templateName,
                    nextDate: activityData.nextDate as string,
                    cost: activityData.cost as number | undefined,
                    costSource: activityData.costSource as CostSource | undefined,
                    intervalType: activityData.intervalType as string | undefined,
                    intervalValue: activityData.intervalValue as number | undefined,
                });
            }

            // Sort by nextDate ASC
            return activities.sort((a, b) => a.nextDate.localeCompare(b.nextDate));
        } catch (err) {
            console.error('Failed to load maintenance activities:', err);
            return [];
        }
    }, [db, buildingKey, componentKey]);

    // Load the checks about this component (their componentKey)
    const loadInspections = useCallback(async (): Promise<BuildingCheckWithTemplate[]> => {
        try {
            const [checksSnapshot, templatesSnapshot] = await Promise.all([
                get(ref(db, `inspections/active/${buildingKey}`)),
                get(ref(db, 'inspections/templates'))
            ]);

            if (!checksSnapshot.exists()) {
                return [];
            }

            const checksData = checksSnapshot.val();
            const templatesData = templatesSnapshot.exists() ? templatesSnapshot.val() : {};

            const inspections: BuildingCheckWithTemplate[] = [];

            for (const [checkKey, checkData] of Object.entries(checksData)) {
                const check = parseBuildingCheck(checkKey, checkData);
                if (!check) continue;
                if (check.componentKey !== componentKey) continue;

                const template = templatesData[check.templateId] as BuildingCheckTemplate | undefined;

                inspections.push({
                    ...check,
                    checkKey,
                    template: template ? { ...template, id: check.templateId } : { id: check.templateId, name: check.templateId } as BuildingCheckTemplate,
                    buildingId: buildingKey,
                });
            }

            // Sort by nextDueDate ASC
            return inspections.sort((a, b) => a.nextDueDate.localeCompare(b.nextDueDate));
        } catch (err) {
            console.error('Failed to load inspections:', err);
            return [];
        }
    }, [db, buildingKey, componentKey]);

    // Load all active data in parallel
    const loadActiveData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const [workOrders, issues, maintenance, inspections, projects] = await Promise.all([
                workOrdersApi.getByBuilding(buildingKey),
                issuesApi.getByBuilding(buildingKey),
                loadMaintenanceActivities(),
                loadInspections(),
                projectsApi.getProjectsByBuilding(buildingKey),
            ]);

            setAllWorkOrders(workOrders);
            setAllIssues(issues);
            setMaintenanceActivities(maintenance);
            setAllInspections(inspections);
            setAllProjects(projects);
        } catch (err) {
            console.error('Failed to load component related items:', err);
            setError('Kunde inte ladda relaterade aktiviteter');
        } finally {
            setIsLoading(false);
        }
    }, [buildingKey, loadMaintenanceActivities, loadInspections]);

    // Load archived data (lazy - only when toggle is switched on)
    const loadArchivedData = useCallback(async () => {
        if (archivedLoaded) return;

        setIsLoadingArchived(true);
        try {
            const [archivedWos, archivedIss] = await Promise.all([
                workOrdersApi.getArchivedByBuilding(buildingKey),
                issuesApi.getArchivedByBuilding(buildingKey),
            ]);

            setArchivedWorkOrders(archivedWos);
            setArchivedIssues(archivedIss);
            setArchivedLoaded(true);
        } catch (err) {
            console.error('Failed to load archived data:', err);
            // Don't set error - partial failure, show active data still
        } finally {
            setIsLoadingArchived(false);
        }
    }, [buildingKey, archivedLoaded]);

    // Initial load
    useEffect(() => {
        loadActiveData();
    }, [loadActiveData]);

    // Load archived data when toggle is switched on
    useEffect(() => {
        if (showArchived && !archivedLoaded) {
            loadArchivedData();
        }
    }, [showArchived, archivedLoaded, loadArchivedData]);

    // Filter work orders by componentKey
    const workOrders = useMemo(() => {
        const filtered = allWorkOrders.filter(wo =>
            getComponentKeys(wo).includes(componentKey) && wo.status === WorkOrderStatus.OPEN
        );
        // Sort by dueDate ASC (soonest first), then createdAt DESC
        return filtered.sort((a, b) => {
            if (a.dueDate && b.dueDate) {
                return a.dueDate.localeCompare(b.dueDate);
            }
            if (a.dueDate) return -1;
            if (b.dueDate) return 1;
            return b.createdAt.localeCompare(a.createdAt);
        });
    }, [allWorkOrders, componentKey]);

    // Filter issues by componentKey, excluding converted issues
    const issues = useMemo(() => {
        const filtered = allIssues.filter(issue =>
            issue.componentKey === componentKey && !issue.convertedToWorkOrderKey
        );
        // Sort by priority DESC (high first), then createdAt DESC
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return filtered.sort((a, b) => {
            const priorityDiff = (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
            if (priorityDiff !== 0) return priorityDiff;
            return b.createdAt.localeCompare(a.createdAt);
        });
    }, [allIssues, componentKey]);

    // Filter archived work orders by componentKey
    const filteredArchivedWorkOrders = useMemo(() => {
        return archivedWorkOrders.filter(wo => getComponentKeys(wo).includes(componentKey));
    }, [archivedWorkOrders, componentKey]);

    // Filter archived issues by componentKey, excluding converted
    const filteredArchivedIssues = useMemo(() => {
        return archivedIssues.filter(issue =>
            issue.componentKey === componentKey && !issue.convertedToWorkOrderKey
        );
    }, [archivedIssues, componentKey]);

    // Derive projects from work orders that have both componentKey and projectKey
    const projects = useMemo(() => {
        // Get unique projectKeys from work orders linked to this component
        const projectKeys = new Set<string>();

        // Check active work orders
        allWorkOrders.forEach(wo => {
            if (getComponentKeys(wo).includes(componentKey) && wo.projectKey) {
                projectKeys.add(wo.projectKey);
            }
        });

        // Also check archived work orders if loaded
        if (archivedLoaded) {
            archivedWorkOrders.forEach(wo => {
                if (getComponentKeys(wo).includes(componentKey) && wo.projectKey) {
                    projectKeys.add(wo.projectKey);
                }
            });
        }

        // Filter allProjects to only those linked via work orders
        const linkedProjects = allProjects.filter(p => projectKeys.has(p.key));

        // Sort: IN_PROGRESS first, then PLANNED, then COMPLETED
        const statusOrder: Record<ProjectStatus, number> = {
            [ProjectStatus.IN_PROGRESS]: 0,
            [ProjectStatus.PLANNED]: 1,
            [ProjectStatus.COMPLETED]: 2,
        };

        return linkedProjects.sort((a, b) => {
            const statusDiff = (statusOrder[a.status] ?? 2) - (statusOrder[b.status] ?? 2);
            if (statusDiff !== 0) return statusDiff;
            return a.startDate.localeCompare(b.startDate);
        });
    }, [allWorkOrders, archivedWorkOrders, archivedLoaded, allProjects, componentKey]);

    // Compute hasAnyItems
    const hasAnyItems = useMemo(() => {
        return (
            workOrders.length > 0 ||
            issues.length > 0 ||
            maintenanceActivities.length > 0 ||
            allInspections.length > 0 ||
            projects.length > 0
        );
    }, [workOrders, issues, maintenanceActivities, allInspections, projects]);

    // Compute hasArchivedItems
    const hasArchivedItems = useMemo(() => {
        return filteredArchivedWorkOrders.length > 0 || filteredArchivedIssues.length > 0;
    }, [filteredArchivedWorkOrders, filteredArchivedIssues]);

    // Refetch function
    const refetch = useCallback(async () => {
        setArchivedLoaded(false);
        await loadActiveData();
        if (showArchived) {
            await loadArchivedData();
        }
    }, [loadActiveData, loadArchivedData, showArchived]);

    return {
        workOrders,
        issues,
        maintenanceActivities,
        inspections: allInspections,
        projects,
        archivedWorkOrders: filteredArchivedWorkOrders,
        archivedIssues: filteredArchivedIssues,
        isLoading,
        isLoadingArchived,
        error,
        showArchived,
        setShowArchived,
        refetch,
        hasAnyItems,
        hasArchivedItems,
    };
}
